<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Button from "../../components/input/Button.svelte";

  let { trip, blogPost, photos = [], schedules = [] }: { trip: any; blogPost: any; photos?: any[]; schedules?: any[] } = $props();
  let title = $state("");
  let body = $state("");
  let preview = $state(false);
  let saving = $state(false);
  let publishing = $state(false);
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let editorHydrated = false;

  $effect(() => {
    if (editorHydrated) return;
    title = blogPost.title || "";
    body = blogPost.body || "";
    editorHydrated = true;
  });

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  async function saveNow() {
    saving = true;
    await fetch(`/api/v1/trips/${trip.id}/blog_posts/${blogPost.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ blog_post: { title, body } })
    });
    saving = false;
  }

  function queueSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(saveNow, 2000);
  }

  async function publish() {
    publishing = true;
    await saveNow();
    await fetch(`/api/v1/trips/${trip.id}/blog_posts/${blogPost.id}/publish`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() }
    });
    publishing = false;
    router.visit(`/trips/${trip.id}/blog`);
  }

  async function exportPost(format: "markdown" | "html") {
    const response = await fetch(`/api/v1/trips/${trip.id}/blog_posts/${blogPost.id}/export?format=${format}`);
    const json = await response.json();
    const blob = new Blob([json.data.content], { type: format === "html" ? "text/html" : "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title || "blog-post"}.${format === "html" ? "html" : "md"}`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function insertImage(photo: any) {
    body = `${body}\n\n![${photo.caption || "trip photo"}](${photo.image_url})`;
    queueSave();
  }
</script>

<svelte:head>
  <title>{title || "Blog Editor"}</title>
</svelte:head>

<TripSubNav {trip} currentPage="blog" />

<div class="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
  <section class="rounded-3xl border border-white/10 bg-white/5 p-5">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.2em] text-muted">Blog Editor</p>
        <input bind:value={title} class="mt-2 w-full bg-transparent text-3xl font-semibold outline-none" placeholder="제목을 입력하세요" oninput={queueSave} />
      </div>
      <div class="flex items-center gap-2">
        <Button variant="secondary" size="sm" onclick={() => (preview = !preview)}>{preview ? "편집" : "미리보기"}</Button>
        <Button variant="secondary" size="sm" onclick={() => exportPost("markdown")}>Markdown</Button>
        <Button variant="secondary" size="sm" onclick={() => exportPost("html")}>HTML</Button>
        <Button variant="primary" size="sm" loading={publishing} onclick={publish}>발행</Button>
      </div>
    </div>
    <p class="mt-3 text-sm text-muted">{saving ? "자동 저장 중..." : "2초 후 자동 저장"}</p>
  </section>

  <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
    <section class="rounded-3xl border border-white/10 bg-slate-950/80 p-5">
      {#if preview}
        <article class="prose prose-invert max-w-none whitespace-pre-wrap">{body}</article>
      {:else}
        <textarea bind:value={body} class="min-h-[520px] w-full resize-none bg-transparent outline-none" placeholder="본문을 작성하세요" oninput={queueSave}></textarea>
      {/if}
    </section>

    <aside class="space-y-6">
      <section class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <h2 class="text-sm font-medium text-muted">사진 삽입</h2>
        <div class="mt-4 grid grid-cols-2 gap-3">
          {#each photos as photo}
            <button type="button" class="overflow-hidden rounded-2xl border border-white/10" onclick={() => insertImage(photo)}>
              <img src={photo.thumbnail_url || photo.image_url} alt={photo.caption || "trip photo"} class="aspect-square w-full object-cover" />
            </button>
          {/each}
        </div>
      </section>

      <section class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <h2 class="text-sm font-medium text-muted">참조 일정</h2>
        <div class="mt-4 space-y-3">
          {#each schedules as schedule}
            <div class="rounded-2xl bg-slate-900/70 p-3 text-sm">
              <p class="font-medium">{schedule.title}</p>
              <p class="mt-1 text-xs text-muted">{schedule.start_time || schedule.date}</p>
            </div>
          {/each}
        </div>
      </section>
    </aside>
  </div>
</div>
